import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  FormControl,
  Grid2,
  Icon,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import {
  useGetUserNotificationPreferences,
  useGetPlantNotificationPreferences,
  useGetVapidKey,
  useSubscribePush,
  useUnsubscribePush,
  useUpdateUserNotificationPreference,
  useUpdatePlantNotificationPreference,
} from "api";
import { queryClient } from "api/setup";
import { Widget } from "components/elements/mui";
import { PageLayout } from "components/layouts";
import { useFormatMessage } from "lang";
import {
  getCurrentSubscription,
  isPushSupported,
  requestPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
} from "utils/pushSubscription";

const switchColor = "success" as const;

export const NotificationsPage = () => {
  const formatMessage = useFormatMessage();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [permissionState, setPermissionState] =
    useState<NotificationPermission>("default");
  const [loading, setLoading] = useState(false);
  const [selectedMeterId, setSelectedMeterId] = useState<string>("");
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null,
  );

  const { data: vapidData } = useGetVapidKey();
  const { data: userPrefs, isLoading: userPrefsLoading } =
    useGetUserNotificationPreferences();
  const { data: plantPrefs, isLoading: plantPrefsLoading } =
    useGetPlantNotificationPreferences();
  const prefsLoading = userPrefsLoading || plantPrefsLoading;
  const subscribeMutation = useSubscribePush();
  const unsubscribeMutation = useUnsubscribePush();
  const updateUserPrefMutation = useUpdateUserNotificationPreference();
  const updatePlantPrefMutation = useUpdatePlantNotificationPreference();

  useEffect(() => {
    if (!isPushSupported()) return;
    setPermissionState(Notification.permission);

    getCurrentSubscription().then((sub) => {
      setPushEnabled(!!sub);
    });
  }, []);

  useEffect(() => {
    if (plantPrefs?.length && !selectedMeterId) {
      setSelectedMeterId(plantPrefs[0].powerPlantMeterId);
    }
  }, [plantPrefs, selectedMeterId]);

  const handleTogglePush = useCallback(async () => {
    if (!vapidData?.publicKey) return;
    setLoading(true);

    try {
      if (pushEnabled) {
        const sub = await getCurrentSubscription();
        if (sub) {
          await unsubscribeMutation.mutateAsync(sub.endpoint);
          await unsubscribeFromPush();
        }
        setPushEnabled(false);
      } else {
        const permission = await requestPushPermission();
        setPermissionState(permission);

        if (permission === "granted") {
          const subscription = await subscribeToPush(vapidData.publicKey);
          if (subscription) {
            await subscribeMutation.mutateAsync(subscription);
            setPushEnabled(true);
          }
        }
      }
    } catch (error) {
      console.error("Error toggling push notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [
    pushEnabled,
    vapidData?.publicKey,
    subscribeMutation,
    unsubscribeMutation,
  ]);

  const handleUpdateUserPref = useCallback(
    async (field: keyof NonNullable<typeof userPrefs>, value: boolean) => {
      await updateUserPrefMutation.mutateAsync({ [field]: value });
      queryClient.invalidateQueries({
        queryKey: ["userNotificationPreferences"],
      });
    },
    [updateUserPrefMutation],
  );

  const handleUpdatePlantPref = useCallback(
    async (
      meterId: string,
      field: "statusChanges" | "dailySummary" | "curtailmentAdvice" | "settlementAdvice",
      value: boolean,
    ) => {
      await updatePlantPrefMutation.mutateAsync({ meterId, [field]: value });
      queryClient.invalidateQueries({
        queryKey: ["plantNotificationPreferences"],
      });
    },
    [updatePlantPrefMutation],
  );

  const selectedPref = plantPrefs?.find(
    (p) => p.powerPlantMeterId === selectedMeterId,
  );

  return (
    <PageLayout pageTitle={formatMessage("pageTitle.notifications")}>
      <Grid2 container spacing={2}>
        {!isPushSupported() && (
          <Grid2 size={{ xs: 12 }}>
            <Alert severity="info">
              {formatMessage("notifications.notSupported")}
            </Alert>
          </Grid2>
        )}

        {permissionState === "denied" && (
          <Grid2 size={{ xs: 12 }}>
            <Alert severity="warning">
              {formatMessage("notifications.permissionDenied")}
            </Alert>
          </Grid2>
        )}

        {isPushSupported() && (
          <Widget
            size={{ xs: 12 }}
            content={
              <Stack sx={{ gap: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body1">
                    {formatMessage("notifications.enable")}
                  </Typography>
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Switch
                      color={switchColor}
                      checked={pushEnabled}
                      onChange={handleTogglePush}
                      disabled={permissionState === "denied"}
                    />
                  )}
                </Stack>

                <Collapse in={pushEnabled}>
                  <Stack sx={{ gap: 2 }}>
                    {prefsLoading && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {userPrefs && (
                      <Card variant="outlined">
                        <CardContent>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            {formatMessage("notifications.general")}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            mt={1}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography variant="body2">
                                {formatMessage("notifications.priceChanges")}
                              </Typography>
                              <IconButton
                                size="small"
                                sx={{ p: 0.25, color: "text.secondary" }}
                                onClick={(e) =>
                                  setPopoverAnchor(e.currentTarget)
                                }
                              >
                                <Icon sx={{ fontSize: 18 }}>info_outline</Icon>
                              </IconButton>
                              <Popover
                                open={!!popoverAnchor}
                                anchorEl={popoverAnchor}
                                onClose={() => setPopoverAnchor(null)}
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                              >
                                <Typography
                                  sx={{ p: 1.5, maxWidth: 260 }}
                                  variant="body2"
                                >
                                  {formatMessage(
                                    "notifications.priceChanges.tooltip",
                                  )}
                                </Typography>
                              </Popover>
                            </Stack>
                            <Switch
                              color={switchColor}
                              checked={userPrefs.priceChanges}
                              onChange={(_, checked) =>
                                handleUpdateUserPref("priceChanges", checked)
                              }
                            />
                          </Stack>
                        </CardContent>
                      </Card>
                    )}

                    {plantPrefs && plantPrefs.length > 0 && (
                      <Card variant="outlined">
                        <CardContent>
                          <Stack sx={{ gap: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              {formatMessage("notifications.solarPlant")}
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={selectedMeterId}
                                onChange={(e) =>
                                  setSelectedMeterId(e.target.value)
                                }
                              >
                                {plantPrefs.map((pref) => (
                                  <MenuItem
                                    key={pref.powerPlantMeterId}
                                    value={pref.powerPlantMeterId}
                                  >
                                    {pref.powerPlantName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {selectedPref && (
                              <Stack sx={{ gap: 1 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography variant="body2">
                                    {formatMessage(
                                      "notifications.statusChanges",
                                    )}
                                  </Typography>
                                  <Switch
                                    color={switchColor}
                                    checked={selectedPref.statusChanges}
                                    onChange={(_, checked) =>
                                      handleUpdatePlantPref(
                                        selectedPref.powerPlantMeterId,
                                        "statusChanges",
                                        checked,
                                      )
                                    }
                                  />
                                </Stack>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography variant="body2">
                                    {formatMessage(
                                      "notifications.dailySummary",
                                    )}
                                  </Typography>
                                  <Switch
                                    color={switchColor}
                                    checked={selectedPref.dailySummary}
                                    onChange={(_, checked) =>
                                      handleUpdatePlantPref(
                                        selectedPref.powerPlantMeterId,
                                        "dailySummary",
                                        checked,
                                      )
                                    }
                                  />
                                </Stack>
                                {selectedPref.tradeaAdvice &&
                                  (["curtailmentAdvice", "settlementAdvice"] as const).map((field) => (
                                  <Stack
                                    key={field}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2">
                                      {formatMessage(`notifications.${field}`)}
                                    </Typography>
                                    <Switch
                                      color={switchColor}
                                      checked={selectedPref[field]}
                                      onChange={(_, checked) =>
                                        handleUpdatePlantPref(
                                          selectedPref.powerPlantMeterId,
                                          field,
                                          checked,
                                        )
                                      }
                                    />
                                  </Stack>
                                  ))}
                              </Stack>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                  </Stack>
                </Collapse>
              </Stack>
            }
          />
        )}
      </Grid2>
    </PageLayout>
  );
};
